import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { CalendarX2, Clock, Inbox, Loader2, LogOut, RotateCcw, ShieldCheck, Store, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { TIME_SLOTS } from "@/data/services";
import { SHOP_CATEGORIES, SHOP_PRODUCTS } from "@/data/catalog";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  new: "bg-[#FF4311]/15 text-[#FF4311] border-[#FF4311]/40",
  confirmed: "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/40",
  done: "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/40",
  cancelled: "bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/40",
};

const Badge = ({ status, testid }) => (
  <span data-testid={testid} className={`inline-block text-xs font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border ${STATUS_STYLES[status] || ""}`}>
    {status}
  </span>
);

export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [blockouts, setBlockouts] = useState([]);
  const [filter, setFilter] = useState("");
  const [newBlock, setNewBlock] = useState({ date: "", note: "" });
  const [availDate, setAvailDate] = useState("");
  const [dayInfo, setDayInfo] = useState(null);
  const [customSlot, setCustomSlot] = useState("");
  const [storeItems, setStoreItems] = useState(null);
  const [storeDirty, setStoreDirty] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const load = useCallback(async () => {
    setLoadingData(true);
    try {
      const [s, b, l, bo] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/leads"),
        api.get("/admin/blockouts"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setLeads(l.data);
      setBlockouts(bo.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF4311]" /></div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const setStatus = async (kind, id, status) => {
    try {
      await api.patch(`/admin/${kind}/${id}/status`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addBlockout = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/blockouts", newBlock);
      toast.success("Date blocked out");
      setNewBlock({ date: "", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeBlockout = async (id) => {
    try {
      await api.delete(`/admin/blockouts/${id}`);
      toast.success("Block-out removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadDay = async (d) => {
    setAvailDate(d);
    setDayInfo(null);
    if (!d) return;
    try {
      const { data } = await api.get(`/admin/availability?date=${d}`);
      setDayInfo(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleSlot = (t) => {
    if (!dayInfo || dayInfo.booked_slots.includes(t)) return;
    const on = dayInfo.all_slots.includes(t);
    setDayInfo({ ...dayInfo, all_slots: on ? dayInfo.all_slots.filter((s) => s !== t) : [...dayInfo.all_slots, t] });
  };

  const addCustomSlot = () => {
    const t = customSlot.trim();
    if (!t || !dayInfo || dayInfo.all_slots.includes(t)) return;
    setDayInfo({ ...dayInfo, all_slots: [...dayInfo.all_slots, t] });
    setCustomSlot("");
  };

  const saveDay = async () => {
    try {
      await api.put("/admin/availability", { date: availDate, slots: dayInfo.all_slots });
      toast.success(`Times updated for ${availDate}`);
      loadDay(availDate);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetDay = async () => {
    try {
      await api.delete(`/admin/availability/${availDate}`);
      toast.success("Back to default times");
      loadDay(availDate);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadStore = async () => {
    try {
      const { data } = await api.get("/store");
      setStoreItems(Array.isArray(data.products) && data.products.length ? data.products : SHOP_PRODUCTS.map((p) => ({ ...p })));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateItem = (idx, key, value) => {
    setStoreItems((items) => items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
    setStoreDirty(true);
  };

  const removeItem = (idx) => {
    setStoreItems((items) => items.filter((_, i) => i !== idx));
    setStoreDirty(true);
  };

  const addItem = () => {
    setStoreItems((items) => [...(items || []), { id: `custom-${Date.now()}`, cat: "auto", name: "New Item", varies: true, price: "Call for quote", priceNum: null, desc: "", bullets: [] }]);
    setStoreDirty(true);
  };

  const saveStore = async () => {
    try {
      await api.put("/admin/store", { products: storeItems });
      setStoreDirty(false);
      toast.success("Store updated — live on the booking page");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filteredBookings = filter ? bookings.filter((b) => b.status === filter) : bookings;
  const filteredLeads = filter ? leads.filter((l) => l.status === filter) : leads;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen">
      <header className="border-b border-border jpd-glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8  bg-primary flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" />
            </span>
            <span className="font-display font-bold">Owner Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="admin-user-email" className="hidden sm:inline text-xs text-slate-400">{user.email}</span>
            <Link to="/" data-testid="admin-view-site-link" className="text-sm text-slate-300 hover:text-white transition-colors">View site</Link>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-[#EF4444] transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div data-testid="admin-stats-bar" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total Bookings", value: stats?.total_bookings, testid: "stat-total-bookings" },
            { label: "New Leads", value: stats?.new_leads, testid: "stat-new-leads" },
            { label: "Confirmed", value: stats?.confirmed_bookings, testid: "stat-confirmed-bookings" },
            { label: "Pending Requests", value: stats?.pending_requests, testid: "stat-pending-requests" },
          ].map((s) => (
            <div key={s.label} className="jpd-card p-5">
              <p className="jpd-label">{s.label}</p>
              <p data-testid={s.testid} className="mt-2 font-display text-3xl font-extrabold text-white">
                {s.value ?? "—"}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2">
            {[
              { id: "bookings", label: "Bookings", icon: Inbox },
              { id: "leads", label: "Leads", icon: Inbox },
              { id: "availability", label: "Availability", icon: CalendarX2 },
              { id: "store", label: "Store", icon: Store },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => { setTab(t.id); setFilter(""); if (t.id === "store") loadStore(); }}
                className={`px-4 py-2.5  text-sm font-semibold transition-colors ${
                  tab === t.id ? "bg-primary text-white" : "bg-card border border-border text-slate-300 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {tab !== "availability" && tab !== "store" && (
            <select
              data-testid="admin-status-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="jpd-input !w-auto !py-2.5 text-sm"
            >
              <option value="">All statuses</option>
              <option value="new">New</option>
              <option value="confirmed">Confirmed</option>
              <option value="done">Done</option>
              <option value="cancelled">Cancelled</option>
            </select>
          )}
        </div>

        {loadingData ? (
          <div className="mt-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF4311]" /></div>
        ) : tab === "bookings" ? (
          <div data-testid="admin-bookings-list" className="mt-6 space-y-4">
            {filteredBookings.length === 0 && <Empty text="No bookings yet." />}
            {filteredBookings.map((b) => (
              <div key={b.id} data-testid={`admin-booking-${b.id.slice(0, 8)}`} className="jpd-card p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-base">{b.service_name}</p>
                    <p className="mt-1 text-sm text-slate-400">
                      {b.date} · {b.time_slot} · {b.vehicle?.year} {b.vehicle?.make} {b.vehicle?.model}
                    </p>
                    <p className="mt-1 text-sm text-slate-400">
                      {b.name} · <a className="text-[#FF4311]" href={`tel:${b.phone}`}>{b.phone}</a> · {b.email}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">{b.address}</p>
                    {b.addons?.length > 0 && <p className="mt-1 text-xs text-slate-500">Add-ons: {b.addons.join(", ")}</p>}
                    {b.notes && <p className="mt-1 text-xs text-slate-500">Notes: {b.notes}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-3">
                    <Badge status={b.status} testid="admin-booking-status-badge" />
                    {b.payment_status === "deposit_paid" && (
                      <span data-testid="admin-booking-deposit-paid-badge" className="inline-block text-xs font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border border-green-500/40 text-green-400">Deposit paid</span>
                    )}
                    {b.payment_status === "paid_full" && (
                      <span data-testid="admin-booking-paid-full-badge" className="inline-block text-xs font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border border-green-500/40 text-green-400">Paid in full</span>
                    )}
                    {b.payment_status === "deposit_pending" && (
                      <span data-testid="admin-booking-deposit-pending-badge" className="inline-block text-xs font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border border-yellow-500/40 text-yellow-400">Deposit pending</span>
                    )}
                    {b.price_estimate != null && <p className="font-bold text-[#FF4311]">${b.price_estimate}</p>}
                  </div>
                </div>
                <StatusActions testidPrefix="admin-booking" status={b.status} onSet={(s) => setStatus("bookings", b.id, s)} />
              </div>
            ))}
          </div>
        ) : tab === "store" ? (
          <div data-testid="admin-store-panel" className="mt-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-400">Edit names, prices and descriptions — changes go live on the booking page when you save.</p>
              <div className="flex gap-2.5">
                <button type="button" data-testid="admin-store-add-item" onClick={addItem} className="px-4 py-2.5 border border-border text-sm font-semibold text-slate-300 hover:text-white transition-colors">
                  + Add item
                </button>
                <button type="button" data-testid="admin-store-save" onClick={saveStore} disabled={!storeDirty} className="bg-primary hover:bg-[#C20A0B] disabled:opacity-50 text-white text-sm font-semibold px-6 py-2.5 transition-colors">
                  Save Store
                </button>
              </div>
            </div>
            {storeItems === null && <p className="text-sm text-slate-400">Loading store…</p>}
            {(storeItems || []).map((p, idx) => (
              <div key={p.id} data-testid={`admin-store-item-${p.id}`} className="jpd-card p-5 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input data-testid={`store-name-${p.id}`} value={p.name} onChange={(e) => updateItem(idx, "name", e.target.value)} placeholder="Item name" className="jpd-input md:col-span-2" />
                  <select data-testid={`store-cat-${p.id}`} value={p.cat} onChange={(e) => updateItem(idx, "cat", e.target.value)} className="jpd-input">
                    {SHOP_CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                  <input data-testid={`store-price-${p.id}`} value={p.price} onChange={(e) => updateItem(idx, "price", e.target.value)} placeholder='Price label (e.g. "$275 and up")' className="jpd-input" />
                  <input data-testid={`store-pricenum-${p.id}`} type="number" value={p.priceNum ?? ""} onChange={(e) => updateItem(idx, "priceNum", e.target.value === "" ? null : Number(e.target.value))} placeholder="Base $ (number, optional)" className="jpd-input" />
                  <button type="button" data-testid={`store-delete-${p.id}`} onClick={() => removeItem(idx)} className="px-4 py-2.5 border border-border text-sm text-slate-400 hover:text-[#EF4444] hover:border-[#EF4444]/50 transition-colors inline-flex items-center justify-center gap-2">
                    <Trash2 className="w-4 h-4" /> Remove
                  </button>
                </div>
                <textarea data-testid={`store-desc-${p.id}`} value={p.desc || ""} onChange={(e) => updateItem(idx, "desc", e.target.value)} placeholder="Description (optional)" rows={2} className="jpd-input" />
              </div>
            ))}
          </div>
        ) : tab === "leads" ? (
          <div data-testid="admin-leads-list" className="mt-6 space-y-4">
            {filteredLeads.length === 0 && <Empty text="No leads yet." />}
            {filteredLeads.map((l) => (
              <div key={l.id} data-testid={`admin-lead-${l.id.slice(0, 8)}`} className="jpd-card p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-base">{l.name}</p>
                    <p className="mt-1 text-sm text-slate-400">
                      {l.phone && <a className="text-[#FF4311]" href={`tel:${l.phone}`}>{l.phone}</a>} {l.phone && "·"} {l.email}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      {[l.vehicle, l.service].filter(Boolean).join(" · ") || "General inquiry"}
                    </p>
                    {l.notes && <p className="mt-1 text-xs text-slate-500">{l.notes}</p>}
                  </div>
                  <Badge status={l.status} testid="admin-lead-status-badge" />
                </div>
                <StatusActions testidPrefix="admin-lead" status={l.status} onSet={(s) => setStatus("leads", l.id, s)} />
              </div>
            ))}
          </div>
        ) : (
          <div data-testid="admin-availability-panel" className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <form onSubmit={addBlockout} className="jpd-card p-6">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <CalendarX2 className="w-5 h-5 text-[#FF4311]" /> Block out a date
              </h3>
              <p className="mt-1.5 text-sm text-slate-400">Customers won't be able to book blocked dates.</p>
              <input
                data-testid="admin-blockout-date-picker"
                type="date"
                required
                value={newBlock.date}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })}
                className="jpd-input mt-4 [color-scheme:dark]"
              />
              <input
                data-testid="admin-blockout-note-input"
                value={newBlock.note}
                onChange={(e) => setNewBlock({ ...newBlock, note: e.target.value })}
                placeholder="Note (optional — e.g. Vacation)"
                className="jpd-input mt-3"
              />
              <button data-testid="admin-blockout-add-button" className="mt-4 w-full bg-primary hover:bg-[#C20A0B] text-white font-semibold py-3  transition-colors">
                Block This Date
              </button>
            </form>
            <div className="jpd-card p-6">
              <h3 className="text-lg font-semibold">Blocked dates</h3>
              <div data-testid="admin-blockout-list" className="mt-4 space-y-2.5">
                {blockouts.length === 0 && <Empty text="No blocked dates." />}
                {blockouts.map((b) => (
                  <div key={b.id} className="flex items-center justify-between bg-[#15151A]  px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold">{b.date}</p>
                      {b.note && <p className="text-xs text-slate-500">{b.note}</p>}
                    </div>
                    <button
                      data-testid={`admin-blockout-remove-${b.date}`}
                      onClick={() => removeBlockout(b.id)}
                      className="p-2 text-slate-400 hover:text-[#EF4444] transition-colors"
                      aria-label={`Remove block-out ${b.date}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="jpd-card p-6 lg:col-span-2">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#FF4311]" /> Edit times for a day
              </h3>
              <p className="mt-1.5 text-sm text-slate-400">Pick a day, then tap times to turn them on or off for customers. Slots taken by paid bookings lock automatically.</p>
              <input
                data-testid="admin-availability-date-picker"
                type="date"
                value={availDate}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => loadDay(e.target.value)}
                className="jpd-input mt-4 max-w-xs [color-scheme:dark]"
              />
              {dayInfo && (
                <div className="mt-5">
                  {dayInfo.blocked && (
                    <p data-testid="admin-availability-blocked-note" className="text-sm text-[#F59E0B]">This day is blocked out{dayInfo.blockout_note ? ` (${dayInfo.blockout_note})` : ""} — times below won't show to customers.</p>
                  )}
                  <div data-testid="admin-availability-slots" className="mt-3 flex flex-wrap gap-2.5">
                    {[...new Set([...TIME_SLOTS, ...dayInfo.all_slots, ...dayInfo.booked_slots])].map((t) => {
                      const booked = dayInfo.booked_slots.includes(t);
                      const on = dayInfo.all_slots.includes(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          data-testid={`admin-slot-${t.replace(/[^a-z0-9]/gi, "-")}`}
                          disabled={booked}
                          onClick={() => toggleSlot(t)}
                          className={`px-4 py-2.5 text-sm font-semibold border transition-colors ${booked ? "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/40 cursor-not-allowed" : on ? "bg-primary text-white border-primary" : "bg-transparent text-slate-500 border-border hover:text-white"}`}
                        >
                          {t}{booked ? " · booked" : ""}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-2.5">
                    <input
                      data-testid="admin-custom-slot-input"
                      value={customSlot}
                      onChange={(e) => setCustomSlot(e.target.value)}
                      placeholder="Add a time (e.g. 6:00 PM)"
                      className="jpd-input max-w-[220px]"
                    />
                    <button type="button" data-testid="admin-custom-slot-add" onClick={addCustomSlot} className="px-4 py-2.5 text-sm font-semibold border border-border text-slate-300 hover:text-white hover:border-slate-400 transition-colors">
                      Add time
                    </button>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2.5">
                    <button type="button" data-testid="admin-availability-save" onClick={saveDay} className="bg-primary hover:bg-[#C20A0B] text-white font-semibold px-6 py-3 transition-colors">
                      Save Times for {availDate}
                    </button>
                    {dayInfo.custom && (
                      <button type="button" data-testid="admin-availability-reset" onClick={resetDay} className="px-6 py-3 border border-border text-slate-300 hover:text-white transition-colors inline-flex items-center gap-2">
                        <RotateCcw className="w-4 h-4" /> Reset to default times
                      </button>
                    )}
                  </div>
                </div>
              )}
              {!dayInfo && availDate && <p className="mt-4 text-sm text-slate-400">Loading…</p>}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const StatusActions = ({ status, onSet, testidPrefix }) => (
  <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2">
    {status === "new" && (
      <button data-testid={`${testidPrefix}-confirm-button`} onClick={() => onSet("confirmed")}
        className="text-xs font-semibold px-3.5 py-2  bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/40 hover:bg-[#F59E0B]/25 transition-colors">
        Mark Confirmed
      </button>
    )}
    {(status === "new" || status === "confirmed") && (
      <button data-testid={`${testidPrefix}-done-button`} onClick={() => onSet("done")}
        className="text-xs font-semibold px-3.5 py-2  bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/40 hover:bg-[#10B981]/25 transition-colors">
        Mark Done
      </button>
    )}
    {status !== "cancelled" && status !== "done" && (
      <button data-testid={`${testidPrefix}-cancel-button`} onClick={() => onSet("cancelled")}
        className="text-xs font-semibold px-3.5 py-2  bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30 hover:bg-[#EF4444]/20 transition-colors">
        Cancel
      </button>
    )}
  </div>
);

const Empty = ({ text }) => (
  <div className="jpd-card p-10 text-center text-sm text-slate-500">{text}</div>
);
